import { c as _c } from "react/compiler-runtime";
import { useCallback, useMemo } from 'react';
import { KeyReadingStrip } from '../analyze/key-reading/KeyReadingStrip';
import { DesignPrintSummary } from './steps/DesignPrintSummary';
import { touchWeightReadingRows } from '../analyze/key-reading/touch-weight-reading';
import { TouchWeightChart, type TouchWeightReadingContext } from '../charts/touch-weight-chart/TouchWeightChart';
import { DESIGN_INCLUDES_SPRING_SUPPORT, useTouchWeightPreviewSeries } from './hooks/use-touch-weight-preview-series';
import { type TouchDesignSerie } from '../charts/interfaces';
import { useAnalyzedKeyboard } from '@/hooks/keyboard/use-analyzed-keyboard';
import { useMeasureOptions } from '@/hooks/store/use-measure-options-store';
import { useTranslation } from '@/hooks/use-translation';
import { ADJUSTMENT_THRESHOLD } from '@/lib/piano/touch-design/bench-adjustment';
import { formatGrams } from '@/lib/format/format-weight';
import type { TouchWeightAnalyzedKeyboard } from '@/lib/piano/touch-design/touch-weight-key-analysis';
export interface TouchWeightPreviewProps {
  designedKeyboard: TouchWeightAnalyzedKeyboard;
}
const isWeight = (value: number | undefined): value is number => value !== undefined && !Number.isNaN(value);

/**
 * The keys the design gives a weight to.
 *
 * Before and after are averaged over these alone: a key the design cannot
 * resolve yet — one with no front weight, say — has a "before" but no
 * "after", and counting it on one side only would compare two pianos.
 */
const designedKeysOf = (serie: TouchDesignSerie): ReadonlySet<number> => new Set(serie.data.filter(point => isWeight(point.payload)).map(point => point.number));
const averageOf = (serie: TouchDesignSerie, keys: ReadonlySet<number>): number | null => {
  const values = serie.data.filter(point => keys.has(point.number)).map(point => point.payload).filter(isWeight);
  return values.length === 0 ? null : values.reduce((total, value) => total + value, 0) / values.length;
};
const ComparisonTile = t0 => {
  const $ = _c(17);
  const {
    before,
    label,
    isUnchanged,
    unchangedLabel,
    value
  } = t0;
  let t1;
  if ($[0] !== label) {
    t1 = <span className="text-[13px] text-muted-foreground">{label}</span>;
    $[0] = label;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== before || $[3] !== isUnchanged) {
    t2 = isUnchanged ? null : <span className="font-mono text-[14px] text-ptl-ink-3 line-through">{before}</span>;
    $[2] = before;
    $[3] = isUnchanged;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== value) {
    t3 = <span className="font-mono text-[22px] font-medium print:text-[15px]">{value}</span>;
    $[5] = value;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== isUnchanged || $[8] !== unchangedLabel) {
    t4 = isUnchanged ? <span className="text-[12.5px] text-ptl-ink-3">{unchangedLabel}</span> : null;
    $[7] = isUnchanged;
    $[8] = unchangedLabel;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  let t5;
  if ($[10] !== t2 || $[11] !== t3 || $[12] !== t4) {
    t5 = <span className="flex items-baseline gap-2">{t2}{t3}{t4}</span>;
    $[10] = t2;
    $[11] = t3;
    $[12] = t4;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  let t6;
  if ($[14] !== t1 || $[15] !== t5) {
    t6 = <div className="flex flex-[1_1_200px] flex-col gap-1.5 rounded-md border border-border bg-card px-[18px] py-4 print:gap-0.5 print:px-3 print:py-2">{t1}{t5}</div>;
    $[14] = t1;
    $[15] = t5;
    $[16] = t6;
  } else {
    t6 = $[16];
  }
  return t6;
};

/**
 * The touch the design gives, against the touch the piano has today.
 *
 * Four figures say what changes at a glance; the chart underneath shows the
 * touch the design gives, key by key. Today's balance weight is not drawn
 * behind it: a second cloud of points over the designed one made both of them
 * harder to read.
 */
export const TouchWeightPreview = t0 => {
  const $ = _c(27);
  const {
    designedKeyboard
  } = t0;
  const {
    currentLanguage,
    t
  } = useTranslation();
  const analyzedKeyboard = useAnalyzedKeyboard();
  const {
    useSupportSpringMeasurements
  } = useMeasureOptions();
  const {
    current: currentSeries,
    designed: designedSeries
  } = useTouchWeightPreviewSeries(designedKeyboard, analyzedKeyboard, useSupportSpringMeasurements);
  let t1;
  if ($[0] !== designedSeries.balance) {
    t1 = designedKeysOf(designedSeries.balance);
    $[0] = designedSeries.balance;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const designedKeys = t1;
  let t2;
  if ($[2] !== currentLanguage || $[3] !== currentSeries.balance || $[4] !== currentSeries.down || $[5] !== currentSeries.friction || $[6] !== currentSeries.up || $[7] !== designedKeys || $[8] !== designedSeries.balance || $[9] !== designedSeries.down || $[10] !== designedSeries.friction || $[11] !== designedSeries.up || $[12] !== t) {
    const entries = [{
      current: currentSeries.balance,
      label: t("Average balance weight")
    }, {
      current: currentSeries.down,
      label: t("Average down weight")
    }, {
      current: currentSeries.up,
      label: t("Average up weight")
    }, {
      current: currentSeries.friction,
      label: t("Average friction")
    }] as const;
    const designed = [designedSeries.balance, designedSeries.down, designedSeries.up, designedSeries.friction];
    t2 = entries.map((entry, index) => {
      const before = averageOf(entry.current, designedKeys);
      const after = averageOf(designed[index], designedKeys);
      return {
        before: formatGrams(before, currentLanguage),
        isUnchanged: before !== null && after !== null && Math.abs(after - before) < ADJUSTMENT_THRESHOLD,
        label: entry.label,
        value: formatGrams(after, currentLanguage)
      };
    });
    $[2] = currentLanguage;
    $[3] = currentSeries.balance;
    $[4] = currentSeries.down;
    $[5] = currentSeries.friction;
    $[6] = currentSeries.up;
    $[7] = designedKeys;
    $[8] = designedSeries.balance;
    $[9] = designedSeries.down;
    $[10] = designedSeries.friction;
    $[11] = designedSeries.up;
    $[12] = t;
    $[13] = t2;
  } else {
    t2 = $[13];
  }
  const tiles = t2;
  let t3;
  if ($[14] !== currentLanguage || $[15] !== designedKeyboard) {
    t3 = t4 => {
      const {
        hoveredKey,
        series
      } = t4;
      return <KeyReadingStrip reading={{
        keyNumber: hoveredKey,
        noteName: designedKeyboard.getKeyByNumber(hoveredKey ?? 0)?.name ?? null,
        rows: touchWeightReadingRows(series, hoveredKey, currentLanguage)
      }} />;
    };
    $[14] = currentLanguage;
    $[15] = designedKeyboard;
    $[16] = t3;
  } else {
    t3 = $[16];
  }
  const renderReading = t3;
  let t4;
  if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <DesignPrintSummary />;
    $[17] = t4;
  } else {
    t4 = $[17];
  }
  let t5;
  if ($[18] !== t || $[19] !== tiles) {
    t5 = <div className="flex max-w-[var(--ptl-text-measure)] flex-wrap gap-3 print:max-w-none print:flex-nowrap print:gap-2">{tiles.map(tile => <ComparisonTile key={tile.label} {...tile} unchangedLabel={t("unchanged")} />)}</div>;
    $[18] = t;
    $[19] = tiles;
    $[20] = t5;
  } else {
    t5 = $[20];
  }
  let t6;
  if ($[21] !== designedKeyboard || $[22] !== renderReading) {
    t6 = <TouchWeightChart variant="design" analyzedKeyboard={designedKeyboard} renderReading={renderReading} useSupportSpringMeasurements={DESIGN_INCLUDES_SPRING_SUPPORT} />;
    $[21] = designedKeyboard;
    $[22] = renderReading;
    $[23] = t6;
  } else {
    t6 = $[23];
  }
  let t7;
  if ($[24] !== t5 || $[25] !== t6) {
    t7 = <div data-slot="page-wide" className="flex flex-col gap-4">{t4}{t5}{t6}</div>;
    $[24] = t5;
    $[25] = t6;
    $[26] = t7;
  } else {
    t7 = $[26];
  }
  return t7;
};